import { RobotActions, RobotErrorCode, DEVELOPMENT } from '../utils/constants';

export const _getError = (message, code) => ({
  code: code !== undefined ? code : RobotErrorCode.DEFAULT,
  message: message !== undefined ? message : 'robotErrorDefault',
});

/**
 * Normalizes an error to a serializable format for Redux dispatch.
 * Error objects are not serializable and cause Redux DevTools/middleware warnings.
 * @param {Error|{message?: string, code?: number}|number} err - Raw error, serializable error object, or error code
 * @returns {{code: number, message: string}} Serializable error object
 */
export const _normalizeErrorForDispatch = (err) => {
  if (err == null) return _getError('robotErrorDefault');
  if (typeof err === 'number') return _getError('robotErrorDefault', err);
  const message = err?.message ?? String(err);
  const code = err?.code ?? RobotErrorCode.DEFAULT;
  return _getError(message, code);
};

export const isFirmwareTransferRetryState = (firmwareState) =>
  firmwareState === RobotActions.FIRMWARE_UPDATE.FAILED || firmwareState === RobotActions.FIRMWARE_UPDATE.BLE_TRANSFER_EXHAUSTED;

export const _getErrorForCode = (errorCode, robotName) => {
  // Use these error message internally and to show errors in the console.
  // Any loc should happen when we show errors to users since not all these errors are public facing.
  switch (errorCode) {
    case RobotErrorCode.NO_ROBOT:
      return _getError(['robotErrorNoRobot', robotName], errorCode);
    case RobotErrorCode.CANCELLED:
      return _getError(['robotErrorCancelled', robotName], errorCode);
    case RobotErrorCode.NO_BLUETOOTH:
      return _getError(['connectionFailureNoBluetoothBody', robotName], errorCode);
    case RobotErrorCode.NOT_SUPPORTED:
      return _getError(['connectionFailureNotSupportedBody', robotName], errorCode);
    case RobotErrorCode.NO_INTERNET:
      return _getError(['robotErrorConnection', robotName], errorCode);
    case RobotErrorCode.UNSUPPORTED_FIRMWARE:
      return _getError(['robotErrorUnsupportedFirmware', robotName], errorCode);
    case RobotErrorCode.NO_BLE:
      return _getError(['connectionFailureNoBLEBody', robotName], errorCode);
    case RobotErrorCode.BLUETOOTH_DISABLED:
      return _getError(['connectionFailureBluetoothDisabledBody', robotName], errorCode);
    case RobotErrorCode.V2_FIRMWARE_OUTDATED:
      return _getError(['v2RobotFirmwareUpdateRequired', robotName], errorCode);
    case RobotErrorCode.FIRMWARE_VERSIONS_FAILED:
      return _getError(['robotErrorFirmwareVersionsFailed', robotName], errorCode);
    default:
      return _getError(['robotErrorDefault', robotName], errorCode);
  }
};

export const _getErrorForResponse = (response, command) => {
  const message = `Command failed! ${command}; status = ${response.status}`;
  return _getError(message);
};

export const _logMessage = (message, err) => {
  const newMessage = `[RobotService] ${message}`;
  if (typeof err !== 'undefined') {
    console.error(newMessage, err);
  } else if (DEVELOPMENT) {
    console.info(newMessage);
  }
};

/**
 * Log generic SDK errors.
 */
export const _onErrorHandler = (err) => {
  _logMessage('SDK Produced an error:', err.message);
};
